/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form-6';
import { track } from '@fff-web/fff-analytics';
import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared/Toast';
import startCase from 'lodash/startCase';
import toUpper from 'lodash/toUpper';
import get from 'lodash/get';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import toNumber from 'lodash/toNumber';
import size from 'lodash/size';
import toLower from 'lodash/toLower';

import Button from 'pages/MyAccount/shared-components/Button/Button';
import { AdminInput, AdminSelect } from 'pages/MyAccount/shared-components';
import {
  updateShippingInfo,
  clearUserCache,
} from 'pages/MyAccount/services/api/account';
import {
  validateAddress,
  getStatesByCountryId,
} from 'pages/MyAccount/services/api/location';
import {
  handleValidation,
  validateCharacterLimit,
} from 'pages/MyAccount/utils/admin-forms';
import {
  ACCOUNT_INFO,
  ACCOUNT_SECTIONS,
  ACCOUNT_TRACK_NAMES,
} from '../utils/segment';
import { formatPhoneNumber, COUNTRY_SELECTION } from '../utils';
import VerificationModal from './VerificationModal/VerificationModal';
import styles from './Section.module.css';
import shippingStyles from './ShippingAddress.module.css';

const UNITED_KINGDOM_ID = '76';
const CHARACTER_LIMIT = '31';

const BUTTON_CTAS = {
  edit: 'Edit',
  cancel: 'Cancel',
  save: 'SAVE',
};

const ShippingAddresses = ({
  firstName,
  lastName,
  phone,
  address1,
  address2,
  city,
  region,
  zipcode,
  country,
  updateUserInfo,
}) => {
  const defaultValues = {
    firstName: startCase(toLower(firstName)),
    lastName: startCase(toLower(lastName)),
    phone: formatPhoneNumber(phone),
    address1: startCase(toLower(address1)),
    address2: startCase(toLower(address2)),
    city: startCase(toLower(city)),
    region: toUpper(region),
    country,
    zipcode,
  };
  const {
    register,
    handleSubmit,
    errors = {},
    reset,
    formState,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm({
    defaultValues,
  });

  const [open, setOpen] = useState(false);
  const [stateList, setStateList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittedData, setSubmittedData] = useState({});
  const [isShippingUpdated, setShippingUpdated] = useState();
  const [verificationModalOpen, setVerificationModalOpen] = useState(null);
  const [verificationModalData, setVerificationModalData] = useState({
    submitted: {},
    verified: {},
  });
  const { dirtyFields } = formState;
  const countryId = watch('country');
  const unitedKingdomSelected = countryId === UNITED_KINGDOM_ID;

  const getRegions = async () => {
    try {
      const listOfStates = await getStatesByCountryId(countryId);
      console.log('\n\nlistOfStates', listOfStates)
      setStateList(listOfStates);
      find(listOfStates, (st) => {
        const code = get(st, ['stateCode']);
        if (toUpper(code) === toUpper(region)) {
          setValue('region', st.id);
        }
      });
    } catch (e) {
      showErrorToast('Not able to get list of states');
    }
  };

  useEffect(() => {
    if (isShippingUpdated) {
      updateUserInfo(submittedData);
      reset({ ...submittedData });
    }
  }, [submittedData, reset, isShippingUpdated]);

  useEffect(() => {
    if (open) {
      const id = findKey(COUNTRY_SELECTION, [
        'value',
        startCase(toLower(country)),
      ]);
      setValue('country', id);
      setValue('region', id);
    }
  }, [country, open]);

  useEffect(() => {
    if (countryId > 0) {
      getRegions();
    }
  }, [countryId]);

  const toggleOpenSection = () => {
    setOpen((prevState) => {
      const newState = !prevState;
      if (newState) reset(defaultValues);
      return newState;
    });
  };

  const closeVerificationModal = () => {
    setVerificationModalOpen(null);
    setVerificationModalData({
      submitted: {},
      verified: {},
    });
  };

  const getUpdatedData = (data) => {
    return {
      shipAddress1: dirtyFields.address1 && data.address1,
      shipAddress2: dirtyFields.address2 && data.address2,
      shipCity: dirtyFields.city && data.city,
      shipState: unitedKingdomSelected
        ? ''
        : dirtyFields.region &&
          get(find(stateList, ['id', toNumber(data.region)]), 'stateCode'),
      shipZipCode: dirtyFields.zipcode && data.zipcode,
      shipCountry:
        dirtyFields.country && get(COUNTRY_SELECTION, [data.country, 'code']),
      shipPhone: dirtyFields.phone && data.phone,
      shipFirstName: dirtyFields.firstName && data.firstName,
      shipLastName: dirtyFields.lastName && data.lastName,
    };
  };

  const getValidationData = (data) => ({
    primaryLine: data.address1,
    secondaryLine: data.address2,
    city: data.city,
    state: get(find(stateList, ['id', toNumber(data.region)]), 'stateCode'),
    countryCode: get(COUNTRY_SELECTION, [data.country, 'code']),
    zip: data.zipcode,
  });

  const handleAddressValidation = (deliveryStatus, fffUndeliverable) => {
    if (deliveryStatus === 'deliverable') {
      if (fffUndeliverable) return 'suggested';
      return 'success';
    }
    return 'undeliverable';
  };

  const updateShippingAddress = async (data, callback) => {
    try {
      setLoading(true);
      const response = await updateShippingInfo(data);
      if (response.success) {
        clearUserCache();
        setShippingUpdated(true);
        setLoading(false);
        showSuccessToast(
          'Primary Address Updated',
          'Your primary shipping address changes have been successfully saved.'
        );
        track({
          trackName: ACCOUNT_TRACK_NAMES.completed,
          properties: {
            cta_name: BUTTON_CTAS.save,
            account_section: ACCOUNT_SECTIONS.shipping,
            info_updated: ACCOUNT_INFO.address,
          },
        });
        toggleOpenSection();
        if (callback) callback();
      }
    } catch (e) {
      setShippingUpdated(false);
      setLoading(false);
      showErrorToast(
        'Primary Address Update Failed',
        'Your primary shipping address was not updated. Please try again.'
      );
    }
  };

  const onSubmit = async (data) => {
    try {
      const validationData = getValidationData(data);
      setLoading(true);
      const validateResp = await validateAddress(validationData);
      if (validateResp.success) {
        const { deliveryStatus, submitted } = validateResp;
        const { fffUndeliverable } = submitted;
        const validation = handleAddressValidation(
          deliveryStatus,
          fffUndeliverable
        );
        setLoading(false);
        switch (validation) {
          case 'success': {
            const updatedData = getUpdatedData(data);
            updateShippingAddress(updatedData, () => {
              setSubmittedData({
                ...data,
                country: get(COUNTRY_SELECTION, [data.country, 'value']),
                region: get(
                  find(stateList, ['id', toNumber(data.region)]),
                  'stateCode'
                ),
              });
            });
            break;
          }
          case 'suggested':
            setVerificationModalOpen('suggested');
            setVerificationModalData(validateResp);
            break;
          default:
            // undeliverable
            setVerificationModalOpen('undeliverable');
            setVerificationModalData(validateResp);
        }
      }
    } catch (e) {
      setLoading(false);
      showErrorToast(
        'Address Change Unsuccessful',
        "We're experiencing some technical difficulties. Please try again later."
      );
    }
  };

  const handleClickUseAsEntered = () => {
    const formData = getValues();
    const updatedData = getUpdatedData(formData);
    updateShippingAddress(updatedData, () => {
      setSubmittedData({
        ...formData,
        country: get(COUNTRY_SELECTION, [formData.country, 'value']),
        region: get(
          find(stateList, ['id', toNumber(formData.region)]),
          'stateCode'
        ),
      });
      closeVerificationModal();
    });
  };

  const handleClickUseThisAddress = (
    suggestedSelected,
    suggestedData,
    resetSuggestedState
  ) => {
    const formData = getValues();
    const updatedData = getUpdatedData(formData);
    if (suggestedSelected) {
      const data = {
        ...updatedData,
        ...suggestedData,
      };
      updateShippingAddress(data, () => {
        const {
          shipAddress1,
          shipAddress2,
          shipCity,
          shipState,
          shipZipCode,
          shipCountry,
        } = suggestedData;
        setSubmittedData({
          ...formData,
          address1: startCase(toLower(shipAddress1)),
          address2: startCase(toLower(shipAddress2)),
          city: startCase(toLower(shipCity)),
          zipcode: shipZipCode,
          country: get(find(COUNTRY_SELECTION, ['code', shipCountry]), 'value'),
          region: shipState,
        });
        closeVerificationModal();
        resetSuggestedState();
      });
    } else {
      updateShippingAddress(updatedData, () => {
        setSubmittedData({
          ...formData,
          country: get(COUNTRY_SELECTION, [formData.country, 'value']),
          region: get(
            find(stateList, ['id', toNumber(formData.region)]),
            'stateCode'
          ),
        });
        closeVerificationModal();
        resetSuggestedState();
      });
    }
  };

  function getUserCountry() {
    return get(
      find(COUNTRY_SELECTION, ['value', startCase(toLower(country))]),
      'name'
    );
  }

  const displayUserAddess = () => {
    if (defaultValues.address2) {
      return `${defaultValues.address1}, ${defaultValues.address2}`;
    }
    return defaultValues.address1;
  };

  const handleClickEdit = () => {
    if (!open) {
      track({
        trackName: ACCOUNT_TRACK_NAMES.started,
        properties: {
          cta_name: BUTTON_CTAS.edit,
          account_section: ACCOUNT_SECTIONS.shipping,
        },
      });
    }
    toggleOpenSection();
  };

  return (
    <>
      <div className={styles.header}>
        <span>Address</span>
        <button type="button" onClick={handleClickEdit} data-testid="edit-btn-address">
          {open ? BUTTON_CTAS.cancel : BUTTON_CTAS.edit}
        </button>
      </div>
      {open ? (
        <div className={styles.editContainer}>
          <form
            className={shippingStyles.editForm}
            onSubmit={handleSubmit(onSubmit)}
          >
            <h3 className={shippingStyles.header}>Primary Shipping Address</h3>
            <div className={shippingStyles.editInnerContainer}>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="First Name"
                  datatestid="first-name-address-fld"
                  name="firstName"
                  type="text"
                  propRef={register({
                    required: 'This field is required.',
                    pattern: {
                      value: /^[A-ZÀ-ÿa-z\\s,. '\\pL-]+$/,
                      message: 'Please enter a valid first name.',
                    },
                  })}
                  error={errors.firstName && errors.firstName.message}
                  onChange={(e) => {
                    handleValidation(
                      e,
                      setError,
                      clearErrors,
                      validateCharacterLimit
                    );
                  }}
                  characterLimit={CHARACTER_LIMIT}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="Last Name"
                  datatestid="last-name-address-fld"
                  name="lastName"
                  type="text"
                  propRef={register({
                    required: 'This field is required.',
                    pattern: {
                      value: /^[A-ZÀ-ÿa-z\\s,. '\\pL-]+$/,
                      message: 'Please enter a valid last name.',
                    },
                  })}
                  error={errors.lastName && errors.lastName.message}
                  onChange={(e) => {
                    handleValidation(
                      e,
                      setError,
                      clearErrors,
                      validateCharacterLimit
                    );
                  }}
                  characterLimit={CHARACTER_LIMIT}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="Address"
                  datatestid="address-fld"
                  name="address1"
                  type="text"
                  propRef={register({
                    required: 'Please enter a valid address.',
                  })}
                  error={errors.address1 && errors.address1.message}
                  onChange={(e) => {
                    handleValidation(
                      e,
                      setError,
                      clearErrors,
                      validateCharacterLimit
                    );
                  }}
                  characterLimit={CHARACTER_LIMIT}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="Apartment / Suite / Unit etc. (optional)"
                  datatestid="second-address-fld"
                  name="address2"
                  propRef={register()}
                  type="text"
                  onChange={(e) => {
                    handleValidation(
                      e,
                      setError,
                      clearErrors,
                      validateCharacterLimit
                    );
                  }}
                  characterLimit={CHARACTER_LIMIT}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="City / Town"
                  datatestid="city-address-fld"
                  name="city"
                  type="text"
                  propRef={register({
                    required: 'Please enter a valid city.',
                  })}
                  error={errors.city && errors.city.message}
                  onChange={(e) => {
                    handleValidation(
                      e,
                      setError,
                      clearErrors,
                      validateCharacterLimit
                    );
                  }}
                  characterLimit={CHARACTER_LIMIT}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminSelect
                  style={{ width: '100%' }}
                  label="State"
                  selectLabel="Select State"
                  list={stateList}
                  name="region"
                  propRef={register({
                    required: !unitedKingdomSelected,
                  })}
                  disabled={unitedKingdomSelected}
                  error={
                    !unitedKingdomSelected &&
                    errors.region &&
                    'Please enter a valid state.'
                  }
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminSelect
                  style={{ width: '100%' }}
                  label="Country"
                  selectLabel="Select Country"
                  list={COUNTRY_SELECTION}
                  name="country"
                  propRef={register({ required: true })}
                  error={errors.country && 'Please enter a valid country.'}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="Zipcode"
                  datatestid="zipcode-address-fld"
                  name="zipcode"
                  type="text"
                  propRef={register({
                    required: 'Please enter a valid zipcode.',
                  })}
                  error={errors.zipcode && errors.zipcode.message}
                />
              </div>
              <div className={shippingStyles.inputContainer}>
                <AdminInput
                  style={{ width: '100%' }}
                  label="Phone Number"
                  datatestid="phone-number-address-fld"
                  name="phone"
                  type="text"
                  propRef={register({
                    required: 'Please enter a valid phone number.',
                    // I need to make improvement on phone validation
                    // pattern: /^[+]?[(]?[\s]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/i,
                  })}
                  error={errors.phone && errors.phone.message}
                  onChange={(e) => {
                    handleValidation(
                      e,
                      setError,
                      clearErrors,
                      validateCharacterLimit
                    );
                  }}
                  characterLimit={CHARACTER_LIMIT}
                />
              </div>
            </div>
            <div className={styles.btnContainer}>
              <Button
                buttonType="primary"
                className={styles.save}
                type="submit"
                disabled={
                  size(errors) > 0 || size(dirtyFields) === 0 || loading
                }
              >
                {BUTTON_CTAS.save}
              </Button>
              <Button
                className={styles.cancel}
                buttonType="cancel"
                onClick={toggleOpenSection}
                style={{ letterSpacing: '0px' }}
              >
                {BUTTON_CTAS.cancel}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className={styles.readOnlyContainer} data-testid="address-section">
          <div className={shippingStyles.header}>Primary Shipping Address</div>
          <div className={shippingStyles.readOnlySection}>
            <div className={shippingStyles.readOnlyFields}>
              <span data-testid="sa-name">
                {`${firstName} ${lastName}`}
                {' '}
              </span>
              <span data-testid="sa-full-address">
                {displayUserAddess()}
                {' '}
                <br />
                {`${defaultValues.city}, ${defaultValues.region} ${defaultValues.zipcode}`}
              </span>
              <span data-testid="sa-country">{getUserCountry()}</span>
              <span
                data-testid="sa-phone-number"
                className={shippingStyles.phoneNumber}
              >
                {formatPhoneNumber(defaultValues.phone)}
              </span>
            </div>
          </div>
        </div>
      )}
      <VerificationModal
        modal={verificationModalOpen}
        data={verificationModalData}
        loading={loading}
        closeModal={closeVerificationModal}
        onClickUseThisAddress={handleClickUseThisAddress}
        onClickUseAsEntered={handleClickUseAsEntered}
      />
    </>
  );
};

ShippingAddresses.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  phone: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  region: PropTypes.string,
  zipcode: PropTypes.string,
  country: PropTypes.string,
  updateUserInfo: PropTypes.func.isRequired,
};

ShippingAddresses.defaultProps = {
  firstName: '',
  lastName: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  region: '',
  zipcode: '',
  country: '',
};

export default ShippingAddresses;
